import { Workbox } from 'workbox-window';

// Initialize the event for notifying the app about updates
const updateAvailable = new Event('updateAvailable');

// Safe access to environment variables
const getPublicUrl = () => {
  try {
    return (typeof process !== 'undefined' && process.env && process.env.PUBLIC_URL) || '';
  } catch {
    return '';
  }
};

// Function to check for and unregister legacy service workers
async function unregisterLegacyServiceWorkers() {
  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    
    for (const registration of registrations) {
      // Check if this is our old service worker (by script URL)
      if (registration.active && 
          (registration.active.scriptURL.includes('service-worker.js') || 
           registration.active.scriptURL.includes('clearanceCache'))) {
        console.log('Unregistering legacy service worker:', registration);
        await registration.unregister();
        return true; // Indicates we found and unregistered a legacy worker
      }
    }
  }
  return false;
}

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === '[::1]' ||
    // 127.0.0.0/8 are considered localhost for IPv4.
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
);

type Config = {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
};

export function register(config?: Config): void {
  if ((typeof process !== 'undefined' && process.env.NODE_ENV === 'production') && 'serviceWorker' in navigator) {
    // The URL constructor is available in all browsers that support SW.
    const publicUrl = new URL(getPublicUrl(), window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      // Our service worker won't work if PUBLIC_URL is on a different origin
      // from what our page is served on. This might happen if a CDN is used to
      // serve assets; see https://github.com/facebook/create-react-app/issues/2374
      return;
    }

    window.addEventListener('load', () => {
      const swUrl = `${getPublicUrl()}/service-worker.js`;

      if (isLocalhost) {
        // This is running on localhost. Let's check if a service worker still exists or not.
        checkValidServiceWorker(swUrl, config);

        // Add some additional logging to localhost, pointing developers to the
        // service worker/PWA documentation.
        navigator.serviceWorker.ready.then(() => {
          console.log(
            'This web app is being served cache-first by a service ' +
              'worker. To learn more, visit https://cra.link/PWA'
          );
        });
      } else {
        // Is not localhost. Just register service worker
        registerValidSW(swUrl, config);
      }
    });
  }
}

function registerValidSW(swUrl: string, config?: Config): void {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      // Check for updates immediately after registration
      registration.update();

      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          return;
        }
        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // At this point, the updated precached content has been fetched,
              // but the previous service worker will still serve the older
              // content until all client tabs are closed.
              console.log(
                'New content is available and will be used when all ' +
                  'tabs for this page are closed.'
              );

              // Execute callback
              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
            } else {
              // At this point, everything has been precached.
              // It's the perfect time to display a
              // "Content is cached for offline use." message.
              console.log('Content is cached for offline use.');

              // Execute callback
              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        };
      };
    })
    .catch((error) => {
      console.error('Error during service worker registration:', error);
    });
}

function checkValidServiceWorker(swUrl: string, config?: Config): void {
  // Check if the service worker can be found. If it can't reload the page.
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' }
  })
    .then((response) => {
      // Ensure service worker exists, and that we really are getting a JS file.
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        // No service worker found. Probably a different app. Reload the page.
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        // Service worker found. Proceed as normal.
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log('No internet connection found. App is running in offline mode.');
    });
}

export function unregister(): void {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error(error.message);
      });
  }
}

// Create a workbox instance for our app
const wb = new Workbox(`${getPublicUrl()}/service-worker.js`);

// Create a service to handle service worker registration and updates
export const serviceWorkerService = {
  // Register the service worker
  register: async (): Promise<void> => {
    // Wait for the page to fully load before registering
    if (document.readyState === 'complete') {
      await registerServiceWorker();
    } else {
      window.addEventListener('load', registerServiceWorker);
    }
  },
  
  // Force update of service worker
  update: (): void => {
    wb.messageSkipWaiting();
  }
};

// Helper function to register the service worker
async function registerServiceWorker(): Promise<void> {
  // First try to unregister any legacy service workers
  const unregisteredLegacy = await unregisterLegacyServiceWorkers();
  
  // If we unregistered a legacy worker, reload the page to ensure clean state
  if (unregisteredLegacy) {
    window.location.reload();
    return;
  }
  
  // Set up event listener for update found
  wb.addEventListener('waiting', (event) => {
    console.log('New service worker waiting to activate');
    // Dispatch update available event that UpdateAlert component can listen for
    window.dispatchEvent(updateAvailable);
  });
  
  // Register the new service worker
  wb.register()
    .then((registration: any) => {
      console.log('Service worker registered:', registration);
    })
    .catch((error: Error) => {
      console.error('Error during service worker registration:', error);
    });
}